import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Grow,
  Paper,
  CircularProgress,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import {
  MediaRecorderState,
  ProgressModal,
  FeedbackSnackbar,
  stopMediaRecording,
  uploadToScreenApp,
} from './common';
import { config } from '../../../config';

interface InvoiceData {
  invoiceNumber: string;
  date: string;
  dueDate: string;
  billTo: {
    name: string;
    address: string;
    email?: string;
  };
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    amount: number;
  }>;
  subtotal: number;
  tax: number;
  total: number;
  notes?: string;
}

const AudioToInvoice: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<MediaRecorderState>({
    isRecording: false,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState('00:00');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [invoiceData, setInvoiceData] = useState<InvoiceData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const audioContext = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const visualizerRef = useRef<HTMLCanvasElement>(null);
  const animationFrame = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const timerInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const pausedTimeRef = useRef<number>(0);
  const recordedBlob = useRef<Blob | null>(null);
  const audioStream = useRef<MediaStream | null>(null);

  const updateRecordingTime = () => {
    const elapsedTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const minutes = Math.floor(elapsedTime / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (elapsedTime % 60).toString().padStart(2, '0');
    setRecordingTime(`${minutes}:${seconds}`);
  };

  const drawVisualizer = () => {
    if (!visualizerRef.current || !analyser.current) return;

    const canvasCtx = visualizerRef.current.getContext('2d');
    if (!canvasCtx) return;

    const { width, height } = visualizerRef.current;
    const dataArray = new Uint8Array(analyser.current.frequencyBinCount);

    const draw = () => {
      if (!analyser.current) return;

      analyser.current.getByteFrequencyData(dataArray);
      canvasCtx.clearRect(0, 0, width, height);

      const barWidth = Math.ceil(
        (width / analyser.current.frequencyBinCount) * 2.5
      );
      const centerY = height / 2;
      let x = 0;

      canvasCtx.fillStyle = '#d1d5db';
      for (let i = 0; i < analyser.current.frequencyBinCount; i += 1) {
        const barHeight = Math.min(dataArray[i] * 1.5, height);
        canvasCtx.fillRect(
          Math.floor(x),
          Math.floor(centerY - barHeight / 2),
          Math.floor(barWidth),
          Math.floor(barHeight)
        );
        x += barWidth + 1;
      }

      animationFrame.current = requestAnimationFrame(draw);
    };

    draw();
  };

  const generateInvoice = async (audioBlob: Blob) => {
    try {
      console.log('Starting invoice generation process:', {
        blobSize: audioBlob.size,
        blobType: audioBlob.type,
      });

      setIsProcessing(true);
      // Convert audio blob to base64
      const base64Audio = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          console.log('Audio converted to base64:', {
            base64Length: base64.length,
            sampleStart: `${base64.substring(0, 50)}...`,
          });
          resolve(base64);
        };
        reader.onerror = (error) => {
          console.error('Error converting audio to base64:', error);
        };
        reader.readAsDataURL(audioBlob);
      });

      console.log('Sending request to Gemini API...');
      // Send to Gemini API for transcription and invoice generation
      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gemini-1.5-flash-8b',
          contents: [
            {
              parts: [
                {
                  text: `You are an invoice generation assistant. Listen to this audio recording and generate a structured invoice based on the spoken content. Extract all relevant information such as client details, items, quantities, prices, and any additional notes. Format the response as a JSON object with the following structure:
                  {
                    "invoiceNumber": "string",
                    "date": "YYYY-MM-DD",
                    "dueDate": "YYYY-MM-DD",
                    "billTo": {
                      "name": "string",
                      "address": "string",
                      "email": "string"
                    },
                    "items": [
                      {
                        "description": "string",
                        "quantity": number,
                        "unitPrice": number,
                        "amount": number
                      }
                    ],
                    "subtotal": number,
                    "tax": number,
                    "total": number,
                    "notes": "string"
                  }
                  
                  If any information is missing, use reasonable defaults or leave optional fields empty. Ensure all calculations are accurate.`,
                },
                {
                  inlineData: {
                    mimeType: 'audio/webm',
                    data: base64Audio,
                  },
                },
              ],
            },
          ],
        }),
      });

      console.log('Received response from Gemini API:', {
        status: response.status,
        ok: response.ok,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Gemini API error response:', {
          status: response.status,
          statusText: response.statusText,
          errorText,
        });
        throw new Error(`Failed to generate invoice: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Parsed API response:', {
        hasData: !!data,
        hasCandidates: !!data.candidates,
        candidatesLength: data.candidates?.length,
      });

      const result = data.candidates[0].content.parts[0].text;
      console.log('Extracted result text:', {
        resultLength: result.length,
        sampleResult: result.substring(0, 100),
      });

      // Clean up the markdown formatting before parsing JSON
      const cleanJson = result.replace(/```json\n|\n```/g, '').trim();
      console.log('Cleaned JSON:', {
        cleanLength: cleanJson.length,
        sampleClean: cleanJson.substring(0, 100),
      });

      // Parse the JSON response
      const parsedInvoice = JSON.parse(cleanJson);

      // Validate and set default values for numeric fields
      const validatedInvoice = {
        ...parsedInvoice,
        items:
          parsedInvoice.items?.map(
            (item: {
              description: string;
              quantity: number;
              unitPrice: number;
              amount: number;
            }) => ({
              ...item,
              quantity: item.quantity || 0,
              unitPrice: item.unitPrice || 0,
              amount: item.amount || 0,
            })
          ) || [],
        subtotal: parsedInvoice.subtotal || 0,
        tax: parsedInvoice.tax || 0,
        total: parsedInvoice.total || 0,
      };

      console.log('Successfully parsed invoice data:', {
        invoiceNumber: validatedInvoice.invoiceNumber,
        date: validatedInvoice.date,
        itemCount: validatedInvoice.items?.length,
        total: validatedInvoice.total,
      });

      setInvoiceData(validatedInvoice);
    } catch (error) {
      console.error('Invoice generation failed:', {
        error,
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });

      setState((prev) => ({
        ...prev,
        error: 'Failed to generate invoice. Please try again.',
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStream.current = stream;

      audioContext.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      analyser.current = audioContext.current.createAnalyser();
      const source = audioContext.current.createMediaStreamSource(stream);
      source.connect(analyser.current);

      analyser.current.fftSize = 2048;
      analyser.current.minDecibels = -90;
      analyser.current.maxDecibels = -10;
      analyser.current.smoothingTimeConstant = 0.85;

      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000,
      };

      if (MediaRecorder.isTypeSupported(options.mimeType)) {
        mediaRecorder.current = new MediaRecorder(stream, options);
      } else {
        mediaRecorder.current = new MediaRecorder(stream);
      }

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = async () => {
        const audioBlob = new Blob(chunks.current, {
          type: mediaRecorder.current?.mimeType || 'audio/webm;codecs=opus',
        });

        recordedBlob.current = audioBlob;
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        // Generate invoice from the recording
        await generateInvoice(audioBlob);

        setState((prev) => ({
          ...prev,
          isRecording: false,
        }));

        // Clean up
        if (timerInterval.current) clearInterval(timerInterval.current);
        if (visualizerRef.current) visualizerRef.current.style.display = 'none';
        chunks.current = [];
      };

      mediaRecorder.current.start(1000);
      setState((prev) => ({ ...prev, isRecording: true }));
      startTimeRef.current = Date.now();
      updateRecordingTime();
      timerInterval.current = setInterval(updateRecordingTime, 1000);

      if (visualizerRef.current) {
        visualizerRef.current.style.display = 'block';
        drawVisualizer();
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: t('audioToInvoice.messages.microphoneError'),
      }));
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && state.isRecording) {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
      }
      stopMediaRecording(mediaRecorder, [audioStream.current], animationFrame);
    }
  };

  const togglePause = () => {
    if (!mediaRecorder.current) return;

    if (isPaused) {
      mediaRecorder.current.resume();
      startTimeRef.current = Date.now() - pausedTimeRef.current;
      timerInterval.current = setInterval(updateRecordingTime, 1000);
      drawVisualizer();
    } else {
      mediaRecorder.current.pause();
      pausedTimeRef.current = Date.now() - startTimeRef.current;
      if (timerInterval.current) clearInterval(timerInterval.current);
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    }
    setIsPaused(!isPaused);
  };

  const handleReset = () => {
    setState({
      isRecording: false,
      uploading: false,
      progress: 0,
      uploadComplete: false,
      error: null,
    });
    setIsPaused(false);
    setRecordingTime('00:00');
    setInvoiceData(null);
    setIsPlaying(false);
    setIsProcessing(false);

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }

    recordedBlob.current = null;
    chunks.current = [];
    startTimeRef.current = 0;
    pausedTimeRef.current = 0;

    stopMediaRecording(mediaRecorder, [audioStream.current], animationFrame);
    audioStream.current = null;

    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }

    if (audioContext.current?.state !== 'closed') {
      audioContext.current?.close();
    }
    audioContext.current = null;
    analyser.current = null;

    if (visualizerRef.current) {
      const ctx = visualizerRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(
          0,
          0,
          visualizerRef.current.width,
          visualizerRef.current.height
        );
      }
      visualizerRef.current.style.display = 'none';
    }
  };

  useEffect(() => {
    return () => {
      if (timerInterval.current) clearInterval(timerInterval.current);
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, []);

  return (
    <Box sx={{ textAlign: 'center', p: 3, maxWidth: 800, mx: 'auto' }}>
      {state.error?.includes('microphone') && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: 'error.lighter',
            border: '1px solid',
            borderColor: 'error.light',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="error">
            {t('audioToInvoice.messages.microphoneAccess')}
          </Typography>
        </Paper>
      )}

      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        sx={{ mb: 4, minHeight: 56 }}
      >
        {!state.isRecording && !isProcessing && (
          <Button
            variant="contained"
            color="primary"
            onClick={startRecording}
            startIcon={<MicIcon />}
            size="large"
            sx={{
              minWidth: 200,
              px: 4,
              borderRadius: 28,
              py: 2,
              fontSize: '1.1rem',
            }}
          >
            {t('audioToInvoice.buttons.startRecording')}
          </Button>
        )}
        {state.isRecording && (
          <>
            <Grow in={state.isRecording} timeout={300}>
              <Button
                variant="contained"
                color="primary"
                onClick={stopRecording}
                startIcon={<DescriptionIcon />}
                size="large"
                sx={{
                  minWidth: 180,
                  px: 3,
                  borderRadius: 28,
                  py: 2,
                  fontSize: '1.1rem',
                }}
              >
                {t('audioToInvoice.buttons.generateInvoice', {
                  time: recordingTime,
                })}
              </Button>
            </Grow>
            <Grow in={state.isRecording} timeout={300}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={togglePause}
                startIcon={isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                size="large"
                sx={{
                  minWidth: 140,
                  px: 3,
                  borderRadius: 28,
                  py: 2,
                  fontSize: '1.1rem',
                }}
              >
                {isPaused
                  ? t('audioToInvoice.buttons.resume')
                  : t('audioToInvoice.buttons.pause')}
              </Button>
            </Grow>
          </>
        )}
      </Stack>

      <Box
        component="canvas"
        ref={visualizerRef}
        sx={{
          width: '100%',
          height: 150,
          my: 3,
          display: 'none',
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      />

      {isProcessing && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>
            {t('audioToInvoice.messages.generating')}
          </Typography>
        </Box>
      )}

      {invoiceData && (
        <Grow in={!!invoiceData} timeout={500}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mt: 4,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              maxWidth: 600,
              mx: 'auto',
              backgroundColor: 'background.paper',
            }}
          >
            <Stack spacing={3}>
              {audioUrl && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      if (audioRef.current) {
                        if (isPlaying) {
                          audioRef.current.pause();
                        } else {
                          audioRef.current.play();
                        }
                        setIsPlaying(!isPlaying);
                      }
                    }}
                    startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                    sx={{
                      borderRadius: 28,
                      minWidth: 120,
                    }}
                  >
                    {isPlaying
                      ? t('audioToInvoice.buttons.pause')
                      : t('audioToInvoice.buttons.play')}
                  </Button>
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    onEnded={() => setIsPlaying(false)}
                    style={{ display: 'none' }}
                  >
                    <track kind="captions" />
                  </audio>
                </Box>
              )}

              <Typography variant="h5" gutterBottom>
                {t('audioToInvoice.labels.invoiceDetails')}
              </Typography>

              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle1">
                  {t('audioToInvoice.labels.invoiceNumber')}:{' '}
                  {invoiceData.invoiceNumber}
                </Typography>
                <Typography variant="subtitle1">
                  {t('audioToInvoice.labels.date')}: {invoiceData.date}
                </Typography>
                <Typography variant="subtitle1">
                  {t('audioToInvoice.labels.dueDate')}: {invoiceData.dueDate}
                </Typography>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6">
                    {t('audioToInvoice.labels.billTo')}:
                  </Typography>
                  <Typography>{invoiceData.billTo.name}</Typography>
                  <Typography>{invoiceData.billTo.address}</Typography>
                  {invoiceData.billTo.email && (
                    <Typography>{invoiceData.billTo.email}</Typography>
                  )}
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6">
                    {t('audioToInvoice.labels.items')}:
                  </Typography>
                  {invoiceData.items.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 1,
                      }}
                    >
                      <Typography>
                        {item.description} (
                        {t('audioToInvoice.labels.quantity', {
                          count: item.quantity || 0,
                        })}
                        )
                      </Typography>
                      <Typography>${(item.amount || 0).toFixed(2)}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    mt: 3,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    pt: 2,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography>
                      {t('audioToInvoice.labels.subtotal')}:
                    </Typography>
                    <Typography>
                      ${(invoiceData?.subtotal || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography>{t('audioToInvoice.labels.tax')}:</Typography>
                    <Typography>
                      ${(invoiceData?.tax || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      pt: 1,
                      fontWeight: 'bold',
                    }}
                  >
                    <Typography>{t('audioToInvoice.labels.total')}:</Typography>
                    <Typography>
                      ${(invoiceData?.total || 0).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                {invoiceData.notes && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6">
                      {t('audioToInvoice.labels.notes')}:
                    </Typography>
                    <Typography>{invoiceData.notes}</Typography>
                  </Box>
                )}
              </Box>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 3 }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (recordedBlob.current) {
                      uploadToScreenApp(
                        recordedBlob.current,
                        'invoice-recording.webm',
                        () =>
                          setState((prev) => ({ ...prev, uploading: true })),
                        (progress) =>
                          setState((prev) => ({ ...prev, progress })),
                        () =>
                          setState((prev) => ({
                            ...prev,
                            uploadComplete: true,
                          })),
                        (error) => setState((prev) => ({ ...prev, error }))
                      );
                    }
                  }}
                >
                  {t('audioToInvoice.buttons.saveRecording')}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleReset}
                  startIcon={<RestartAltIcon />}
                >
                  {t('audioToInvoice.buttons.startOver')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grow>
      )}

      <ProgressModal
        open={state.uploading}
        progress={state.progress}
        isComplete={state.uploadComplete}
        processingMessage={t('audioToInvoice.messages.saving')}
        completeMessage={t('audioToInvoice.messages.saved')}
        onReset={handleReset}
      />

      <FeedbackSnackbar
        open={!!state.error}
        message={state.error || t('audioToInvoice.messages.error')}
        severity="error"
        onClose={() => setState((prev) => ({ ...prev, error: null }))}
      />
    </Box>
  );
};

export default AudioToInvoice;
